import {EventEmitter, Injectable } from '@angular/core';
import { HubConnection } from '@microsoft/signalr';
import * as ws from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APP_INITIALIZER } from '@angular/core';
import { StoreService } from './store-service.service';
import { filter } from 'rxjs/operators';
import { IWsMessage } from '../interfaces';
import { StateService } from './state.service';
import {IncidentInterface} from "@core/interfaces/incident.interface";

@Injectable()
export class WebsocketService {
  public messageReceived$ = new BehaviorSubject<IWsMessage>(null);
  public incidentCreated$ = new EventEmitter<IncidentInterface>();
  public incidentUpdated$ = new EventEmitter<IncidentInterface>();
  public upConnection = new BehaviorSubject<boolean>(null);
  private connection: HubConnection;
  private timer: any;
  private checkConnectionTimer: any;

  constructor(
    private readonly store: StoreService,
    private readonly state: StateService,
  ) { }

  private initConnection(): void {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
    }
    this.connection = new ws.HubConnectionBuilder()
      .withUrl(`${environment.host}/api/hubs/chat`, {
        accessTokenFactory: () => this.store.getToken(),
        skipNegotiation: true,
        transport: ws.HttpTransportType.WebSockets,
      })
      .configureLogging(ws.LogLevel.Debug)
      .build();

    this.startConnection();
  }

  private initEvents(): void {
    if (this.connection) {
      this.connection.on('ChatMessageReceived', (message: string) => {
        const parsedMessage: IWsMessage = JSON.parse(message);
        this.messageReceived$.next(parsedMessage);
        this.state.hasUnreadMessages.next(parsedMessage.totalUnreadCount);

        if (!environment.production) {
          console.log(`Message received: ${message}`);
        }
      });

      this.connection.on('IncidentCreated', (incident: string) => {
        console.log('INCIDENT CREATED', incident);
        this.incidentCreated$.emit(JSON.parse(incident) as IncidentInterface);
      });

      this.connection.on('IncidentUpdated', (incident) => {
        this.incidentUpdated$.emit(JSON.parse(incident) as IncidentInterface);
      });

      this.connection.onclose(() => {
        if (!environment.production) {
          console.log('Connection closed.');
        }
      });
    }

    this.upConnection
      .pipe(
        filter(value => value !== null),
      )
      .subscribe((value: boolean) => {
        if (value) {
          this.restartConnection();
        } else {
          this.connection.stop();
          clearInterval(this.checkConnectionTimer);
        }
      });
  }

  private restartConnection(): void {
    this.timer = setTimeout(() => this.startConnection(), 5000);
    if (!environment.production) {
      console.log('Restart connection');
    }
  }

  private startConnection(): void {
    if (this.connection && this.connection.state !== ws.HubConnectionState.Connected) {
      this.connection
      .start()
      .then(() => {
        this.checkConnectionState(),
        clearTimeout(this.timer);
      })
      .catch((err) => {
        if (err.message !== 'There was an error with the transport.') {
          this.restartConnection();
        }
      });
    }
  }

  private checkConnectionState(): void {
    this.checkConnectionTimer = setInterval(() => {
      if (!environment.production) {
        console.log(this.connection.state);
      }
      if (!this.upConnection.value) {
        return;
      }
      if (this.connection && this.connection.state !== ws.HubConnectionState.Connected) {
        this.restartConnection();
      }
    }, 15000);
  }

  public init(): Promise<any> {
    return new Promise((resolve: any, reject: any) => {
      this.initConnection();
      this.initEvents();
      resolve();
    });
  }
}

export function websocketFactory(service: WebsocketService): any {
  return (): any => service.init();
}

export const WebsocketInitializer = {
  provide: APP_INITIALIZER,
  useFactory: websocketFactory,
  deps: [WebsocketService],
  multi: true,
};
